/**
 * How many physical "family units" (e.g. carriers) BOM lines imply for a part family.
 * Uses each non-implied part's BOM quantity divided by that part's PartToFamily.packageQuantity.
 *
 * @param {{ familyId: string, parts: Array<{ partFk: string, quantity: number, isImpliedPart?: boolean }> }} family
 * @param {Array<{ part: { id: string, isActive?: boolean, partToFamilies?: Array, PartToFamilies?: Array } }>} expandedParts
 * @returns {number|null} null if unknown or parts disagree
 */
export function getDerivedFamilyUnitCount(family, expandedParts) {
    if (!family || !family.familyId || !family.parts || !family.parts.length) return null;
    const familyIdNorm = String(family.familyId).toLowerCase();
    const ratios = [];

    for (const bomLine of family.parts) {
        if (bomLine.isImpliedPart) continue;
        const expandedPart = expandedParts.find((x) => String(x.part.id) === String(bomLine.partFk));
        if (!expandedPart || expandedPart.part.isActive === false) continue;

        const ptfs = expandedPart.part.partToFamilies || expandedPart.part.PartToFamilies;
        if (!ptfs || !ptfs.length) continue;

        const ptf = ptfs.find((p) => String(p.familyId || p.FamilyId).toLowerCase() === familyIdNorm);
        if (!ptf) continue;

        const rawPkg = parseInt(ptf.packageQuantity ?? ptf.PackageQuantity, 10);
        const packageQty = Number.isFinite(rawPkg) && rawPkg > 0 ? rawPkg : 1;
        const bomQty = Number(bomLine.quantity);
        if (!Number.isFinite(bomQty)) continue;

        ratios.push(bomQty / packageQty);
    }

    if (ratios.length === 0) return null;

    const first = ratios[0];
    const eps = 1e-6;
    const allMatch = ratios.every((r) => Math.abs(r - first) < eps);
    return allMatch ? first : null;
}
